<template>
  <div class="tenant-access-panel">
    <div
      class="
        d-flex
        flex-wrap
        align-center
        justify-space-between
        mb-3
        tenant-access-heading
      "
    >
      <span class="opacity-5 text-dark">
        {{ $t("Inquilinos") }}
      </span>

      <div class="tenant-access-summary">
        <v-chip small class="ma-1" color="primary" text-color="white">
          {{ selecionados.length }} / {{ itensDisponiveis.length }}
          {{ $t("Inquilinos") }}
        </v-chip>
        <v-chip small class="ma-1" color="success" text-color="white">
          {{ totalAtivos }} {{ $t("Ativo") }}
        </v-chip>
        <v-chip small class="ma-1" color="grey" text-color="white">
          {{ totalInativos }} {{ $t("Inativo") }}
        </v-chip>
      </div>
    </div>

    <v-row dense align="center">
      <v-col cols="12" md="9">
        <v-autocomplete
          v-model="inquilinoParaAdicionar"
          :items="inquilinosDisponiveis"
          :item-text="formatarItemInquilino"
          return-object
          :label="$t('Adicionar_inquilino')"
          prepend-inner-icon="mdi-domain"
          cache-items
          hide-details="auto"
          class="
            input-style
            font-size-input
            text-light-input
            placeholder-light
            input-icon
          "
          dense
          flat
          filled
          solo
        >
          <template v-slot:item="{ item }">
            <v-list-item-icon class="mr-3">
              <v-icon color="grey darken-1">mdi-domain</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ nomeInquilino(item) }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="subtituloInquilino(item)">
                {{ subtituloInquilino(item) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="statusInquilino(item)">
              <v-chip
                x-small
                :color="corStatusInquilino(item)"
                text-color="white"
              >
                {{ labelStatusInquilino(item) }}
              </v-chip>
            </v-list-item-action>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" md="3" class="d-flex align-center">
        <v-btn
          class="
            font-weight-bold
            text-none
            btn-primary
            bg-gradient-primary
            px-4
            tenant-add-button
          "
          height="39"
          block
          :disabled="!inquilinoParaAdicionar"
          @click="adicionarInquilino"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t("Adicionar") }}
        </v-btn>
      </v-col>
    </v-row>

    <div v-if="selecionados.length === 0" class="tenant-empty">
      <v-icon color="grey" class="mr-2">mdi-domain-off</v-icon>
      <span>{{ $t("Nenhum_inquilino_selecionado") }}</span>
    </div>

    <div v-else class="tenant-access-list">
      <div
        v-for="item in selecionados"
        :key="chaveInquilino(item)"
        class="tenant-access-row"
      >
        <div class="tenant-access-row-main">
          <v-avatar size="36" color="grey lighten-3" class="mr-3">
            <v-icon color="grey darken-1">mdi-domain</v-icon>
          </v-avatar>

          <div class="tenant-access-row-text">
            <div class="tenant-name">
              {{ nomeInquilino(item) }}
            </div>
            <div v-if="subtituloInquilino(item)" class="tenant-subtitle">
              {{ subtituloInquilino(item) }}
            </div>
          </div>
        </div>

        <div class="tenant-access-row-actions">
          <v-chip
            v-if="statusInquilino(item)"
            small
            :color="corStatusInquilino(item)"
            text-color="white"
          >
            {{ labelStatusInquilino(item) }}
          </v-chip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="error"
                v-bind="attrs"
                v-on="on"
                :disabled="!podeRemoverInquilino(item)"
                :aria-label="$t('Remover')"
                @click="removerInquilinoSelecionado(item)"
              >
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Remover") }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <div v-if="showCurrentTenantControl" class="tenant-current-panel">
      <v-divider class="my-4" />

      <div class="tenant-current-heading">
        <span class="opacity-5 text-dark">
          {{ $t("Inquilino_atual") }}
        </span>
      </div>

      <v-row dense align="center">
        <v-col cols="12" md="8">
          <v-autocomplete
            :value="currentTenantId"
            :items="currentTenantItems"
            :item-text="formatarItemInquilino"
            :item-value="getItemInquilinoId"
            :label="$t('Inquilino')"
            prepend-inner-icon="mdi-domain"
            :disabled="!currentTenantItems.length"
            class="
              input-style
              font-size-input
              text-light-input
              placeholder-light
              input-icon
            "
            dense
            flat
            filled
            solo
            hide-details="auto"
            @input="$emit('update:currentTenantId', $event)"
          />
        </v-col>

        <v-col cols="12" md="4" class="d-flex align-center">
          <v-btn
            class="
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-4
              tenant-current-button
            "
            height="39"
            block
            :loading="loadingCurrentTenant"
            :disabled="!canSubmitCurrentTenant"
            @click="$emit('change-current-tenant')"
          >
            <v-icon left>mdi-swap-horizontal</v-icon>
            {{ $t("Atualizar") }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
export default {
  name: "InquilinosUsuarioPanel",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    minSelected: {
      type: Number,
      default: 0,
    },
    showCurrentTenantControl: {
      type: Boolean,
      default: false,
    },
    currentTenantId: {
      type: [String, Number],
      default: null,
    },
    currentTenantItems: {
      type: Array,
      default: () => [],
    },
    loadingCurrentTenant: {
      type: Boolean,
      default: false,
    },
    canSubmitCurrentTenant: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inquilinoParaAdicionar: null,
    };
  },
  computed: {
    selecionados() {
      return this.normalizarLista(this.value);
    },
    itensDisponiveis() {
      return this.normalizarLista([
        ...(this.items || []),
        ...this.selecionados,
      ]);
    },
    inquilinosDisponiveis() {
      return this.itensDisponiveis.filter((item) => {
        const id = this.normalizarId(this.getItemInquilinoId(item));
        return id !== null && !this.idsSelecionados.has(id);
      });
    },
    idsSelecionados() {
      return new Set(
        this.selecionados
          .map((item) => this.normalizarId(this.getItemInquilinoId(item)))
          .filter((id) => id !== null)
      );
    },
    totalAtivos() {
      return this.selecionados.filter((item) => {
        return this.statusInquilino(item) === "ATIVO";
      }).length;
    },
    totalInativos() {
      return this.selecionados.filter((item) => {
        return this.statusInquilino(item) === "INATIVO";
      }).length;
    },
  },
  watch: {
    inquilinosDisponiveis() {
      const id = this.normalizarId(
        this.getItemInquilinoId(this.inquilinoParaAdicionar)
      );
      if (
        id &&
        !this.inquilinosDisponiveis.some((item) => {
          return this.normalizarId(this.getItemInquilinoId(item)) === id;
        })
      ) {
        this.inquilinoParaAdicionar = null;
      }
    },
  },
  methods: {
    normalizarLista(lista) {
      const mapa = new Map();
      (Array.isArray(lista) ? lista : []).forEach((item, index) => {
        if (!item) {
          return;
        }
        const id = this.normalizarId(this.getItemInquilinoId(item));
        const chave = id !== null ? id : `item-${index}`;
        if (!mapa.has(chave)) {
          mapa.set(chave, item);
        }
      });
      return Array.from(mapa.values());
    },
    normalizarId(id) {
      if (id === null || id === undefined || id === "") {
        return null;
      }
      return String(id);
    },
    getInquilino(item) {
      return item?.inquilino || item || {};
    },
    getItemInquilinoId(item) {
      const inquilino = this.getInquilino(item);
      return inquilino?.id ?? null;
    },
    nomeInquilino(item) {
      const inquilino = this.getInquilino(item);
      const id = this.getItemInquilinoId(item);
      return (
        inquilino?.fantasia ||
        inquilino?.razaoSocial ||
        inquilino?.nome ||
        (id ? `#${id}` : "-")
      );
    },
    subtituloInquilino(item) {
      const inquilino = this.getInquilino(item);
      return (
        inquilino?.nRegistro ||
        inquilino?.nregistro ||
        inquilino?.n_registro ||
        inquilino?.documento ||
        inquilino?.cnpj ||
        ""
      );
    },
    statusInquilino(item) {
      const inquilino = this.getInquilino(item);
      return inquilino?.statusComum || item?.statusComum || "";
    },
    labelStatusInquilino(item) {
      const status = this.statusInquilino(item);
      return status ? this.$t(status) : "";
    },
    corStatusInquilino(item) {
      return this.statusInquilino(item) === "INATIVO" ? "grey" : "success";
    },
    formatarItemInquilino(item) {
      const documento = this.subtituloInquilino(item);
      return documento
        ? `${this.nomeInquilino(item)} - ${documento}`
        : this.nomeInquilino(item);
    },
    chaveInquilino(item) {
      return (
        this.normalizarId(this.getItemInquilinoId(item)) ||
        this.nomeInquilino(item)
      );
    },
    podeRemoverInquilino(item) {
      return (
        !!item &&
        this.selecionados.length > this.minSelected &&
        this.normalizarId(this.getItemInquilinoId(item)) !== null
      );
    },
    emitirSelecionados(lista) {
      const selecionados = this.normalizarLista(lista);
      if (selecionados.length < this.minSelected) {
        this.$emit("input", this.selecionados);
        return;
      }
      this.$emit("input", selecionados);
    },
    adicionarInquilino() {
      if (!this.inquilinoParaAdicionar) {
        return;
      }
      this.emitirSelecionados([
        ...this.selecionados,
        this.inquilinoParaAdicionar,
      ]);
      this.inquilinoParaAdicionar = null;
    },
    removerInquilinoSelecionado(item) {
      if (!this.podeRemoverInquilino(item)) {
        return;
      }
      const idRemover = this.normalizarId(this.getItemInquilinoId(item));
      this.emitirSelecionados(
        this.selecionados.filter((selecionado) => {
          return (
            this.normalizarId(this.getItemInquilinoId(selecionado)) !==
            idRemover
          );
        })
      );
    },
  },
};
</script>

<style scoped>
.tenant-access-panel {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.tenant-access-heading {
  gap: 8px;
}

.tenant-access-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.tenant-add-button {
  min-width: 100%;
}

.tenant-empty {
  align-items: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  display: flex;
  font-size: 0.875rem;
  justify-content: center;
  margin-top: 12px;
  min-height: 56px;
}

.tenant-access-list {
  display: grid;
  gap: 0;
  margin-top: 12px;
}

.tenant-access-row {
  align-items: center;
  border-top: 1px solid #eef2f7;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(180px, 1fr) auto;
  padding: 12px 0;
}

.tenant-access-row:first-child {
  border-top: 0;
}

.tenant-access-row-main {
  align-items: center;
  display: flex;
  min-width: 0;
}

.tenant-access-row-text {
  min-width: 0;
}

.tenant-name {
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.tenant-subtitle {
  color: #64748b;
  font-size: 0.8125rem;
  overflow-wrap: anywhere;
}

.tenant-access-row-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.tenant-current-panel {
  margin-top: 4px;
}

.tenant-current-heading {
  margin-bottom: 12px;
}

.tenant-current-button {
  min-width: 100%;
}

@media (max-width: 960px) {
  .tenant-access-summary {
    justify-content: flex-start;
  }

  .tenant-access-row {
    grid-template-columns: 1fr;
  }

  .tenant-access-row-actions {
    justify-content: flex-start;
  }
}
</style>
